import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "@tiptap/extension-table";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import Youtube from "@tiptap/extension-youtube";
import Mention from "@tiptap/extension-mention";
import { Extension } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ResourceTypes } from "@planzone/dto";
import { common, createLowlight } from "lowlight";

import { MentionList } from "./mentionList";
import { MentionItem } from "./mentionList/types";
import { BoldIcon } from "./boldIcon";
import { ItalicIcon } from "./italicIcon";
import { UnderlineIcon } from "./underlineIcon";
import { StrikethroughIcon } from "./strikethroughIcon";
import { AlignLeftIcon } from "./alignLeftIcon";
import { AlignCenterIcon } from "./alignCenterIcon";
import { AlignRightIcon } from "./alignRightIcon";
import { BulletListIcon } from "./bulletListIcon";
import { OrderedListIcon } from "./orderedListIcon";
import { TaskListIcon } from "./taskListIcon";
import { ColorTextIcon } from "./colorTextIcon";
import { HighlightIcon } from "./highlightIcon";
import { CodeBlockIcon } from "./codeBlockIcon";
import { BlockquoteIcon } from "./blockquoteIcon";
import { TableIcon } from "./tableIcon";
import { ImageIcon } from "./imageIcon";
import { LinkIcon } from "./linkIcon";
import { YoutubeIcon } from "./youtubeIcon";
import { AddColumnBeforeIcon } from "./addColumnBeforeIcon";
import { AddColumnAfterIcon } from "./addColumnAfterIcon";
import { DeleteColumnIcon } from "./deleteColumnIcon";
import { AddRowBeforeIcon } from "./addRowBeforeIcon";
import { AddRowAfterIcon } from "./addRowAfterIcon";
import { DeleteRowIcon } from "./deleteRowIcon";
import { DeleteTableIcon } from "./deleteTableIcon";

import { ToolbarPopover } from "./toolbarPopover";
import { Props, PopoverType } from "./types";
import styles from "./styles.scss";

const lowlight = createLowlight(common);

function RichTextInput({
  placeholder,
  value,
  maxLength = 5000,
  isDisabled = false,
  className,
  mode = "full",
  autoToolbar = false,
  isResizable = false,
  mentions,
  editorRef,
  onEnter,
  input,
  onChange,
  focus,
}: Props) {
  const { t } = useTranslation();
  const [popover, setPopover] = React.useState<PopoverType>(null);
  const [popoverValue, setPopoverValue] = React.useState("");
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const editorWrapperRef = React.useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const [tableToolbarPos, setTableToolbarPos] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const [mentionPopup, setMentionPopup] = React.useState<{
    items: MentionItem[];
    command: (item: MentionItem) => void;
    top: number;
    left: number;
  } | null>(null);
  const mentionListRef = React.useRef<{
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
  } | null>(null);

  const onEnterRef = React.useRef(onEnter);
  onEnterRef.current = onEnter;

  const mentionsRef = React.useRef(mentions);
  mentionsRef.current = mentions;

  const typePriority: Record<string, number> = {
    [ResourceTypes.HUMAN]: 1,
    [ResourceTypes.VIRTUAL]: 2,
    [ResourceTypes.MATERIAL]: 3,
  };

  const mentionSuggestion = React.useMemo(
    () =>
      mentions
        ? {
            items({ query }: { query: string }) {
              return (mentionsRef.current ?? [])
                .filter((item) =>
                  item.label.toLowerCase().includes(query.toLowerCase()),
                )
                .sort((a, b) => a.label.localeCompare(b.label))
                .sort(
                  (a, b) =>
                    (typePriority[a.resource.type] ?? 4) -
                    (typePriority[b.resource.type] ?? 4),
                );
            },
            render() {
              return {
                onStart(props: any) {
                  const rect = props.clientRect?.();
                  const wrapperRect =
                    editorWrapperRef.current?.getBoundingClientRect();
                  if (rect && wrapperRect) {
                    setMentionPopup({
                      items: props.items,
                      command: props.command,
                      top: rect.top - wrapperRect.top,
                      left: rect.left - wrapperRect.left,
                    });
                  }
                },
                onUpdate(props: any) {
                  const rect = props.clientRect?.();
                  const wrapperRect =
                    editorWrapperRef.current?.getBoundingClientRect();
                  if (rect && wrapperRect) {
                    setMentionPopup({
                      items: props.items,
                      command: props.command,
                      top: rect.top - wrapperRect.top,
                      left: rect.left - wrapperRect.left,
                    });
                  }
                },
                onKeyDown(props: any) {
                  if (props.event.key === "Escape") {
                    setMentionPopup(null);
                    return true;
                  }
                  return mentionListRef.current?.onKeyDown(props) ?? false;
                },
                onExit() {
                  setMentionPopup(null);
                },
              };
            },
          }
        : { items: () => [] as MentionItem[], render: () => ({}) },
    [],
  );

  const enterHandlerExtension = onEnter
    ? [
        Extension.create({
          name: "enterHandler",
          addProseMirrorPlugins() {
            return [
              new Plugin({
                key: new PluginKey("enterHandler"),
                props: {
                  handleKeyDown(view, event) {
                    if (
                      event.key === "Enter" &&
                      !event.shiftKey &&
                      !event.metaKey
                    ) {
                      return onEnterRef.current?.() ?? false;
                    }
                    return false;
                  },
                },
              }),
            ];
          },
        }),
      ]
    : [];

  const commentExtensions = [
    StarterKit.configure({ codeBlock: false }),
    Link.configure({ openOnClick: false }),
    Placeholder.configure({ placeholder: placeholder ?? "" }),
    CharacterCount.configure({ limit: maxLength }),
    Underline,
    CodeBlockLowlight.configure({ lowlight }),
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    Mention.configure({
      HTMLAttributes: { class: "mention" },
      suggestion: mentionSuggestion,
    }),
    ...enterHandlerExtension,
  ];

  const fullExtensions = [
    ...commentExtensions,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Image,
    Table.configure({ resizable: true }),
    TableRow,
    TableCell,
    TableHeader,
    TaskList,
    TaskItem.configure({ nested: true }),
    Youtube.configure({ controls: true }),
  ];

  const editor = useEditor({
    extensions: mode === "comment" ? commentExtensions : fullExtensions,
    content: value,
    editable: !isDisabled,
    onUpdate({ editor }) {
      input(editor.getHTML());
    },
    onBlur() {
      setIsFocused(false);
      onChange?.();
    },
    onFocus() {
      setIsFocused(true);
      focus?.();
    },
  });

  React.useEffect(() => {
    if (editorRef && editor) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  // onEnter is handled via the EnterHandler extension in commentExtensions
  // so the suggestion plugin gets priority over it

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isDisabled);
  }, [isDisabled, editor]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        closePopover();
      }
    };
    if (popover) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [popover]);

  React.useEffect(() => {
    if (!editor) return;
    const updateTableToolbar = () => {
      if (!editor.isActive("table")) {
        setTableToolbarPos(null);
        return;
      }
      const { state, view } = editor;
      const { from } = state.selection;
      const domNode = view.nodeDOM(from) as HTMLElement | null;
      const tableEl =
        domNode?.closest?.("table") ??
        (view.domAtPos(from).node as HTMLElement)?.closest?.("table");
      const wrapper = editorWrapperRef.current;
      if (!tableEl || !wrapper) {
        setTableToolbarPos(null);
        return;
      }
      const tableRect = tableEl.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      setTableToolbarPos({
        top: tableRect.top - wrapperRect.top - 4,
        left: tableRect.left - wrapperRect.left,
      });
    };
    editor.on("selectionUpdate", updateTableToolbar);
    editor.on("transaction", updateTableToolbar);
    return () => {
      editor.off("selectionUpdate", updateTableToolbar);
      editor.off("transaction", updateTableToolbar);
    };
  }, [editor]);

  const openPopover = (type: PopoverType) => {
    if (type === "link") {
      const currentUrl = editor?.getAttributes("link").href ?? "";
      setPopoverValue(currentUrl);
    } else {
      setPopoverValue("");
    }
    setPopover(type);
  };

  const closePopover = () => {
    setPopover(null);
    setPopoverValue("");
  };

  const confirmPopover = () => {
    if (!popoverValue.trim()) {
      closePopover();
      return;
    }
    if (popover === "link") {
      editor
        ?.chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: popoverValue })
        .run();
    } else if (popover === "image") {
      editor?.chain().focus().setImage({ src: popoverValue }).run();
    } else if (popover === "youtube") {
      editor?.chain().focus().setYoutubeVideo({ src: popoverValue }).run();
    }
    closePopover();
  };

  const removeLink = () => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    closePopover();
  };

  const charCount = editor?.storage.characterCount.characters() ?? 0;

  const popoverLabels: Record<
    Exclude<PopoverType, null>,
    { title: string; placeholder: string }
  > = {
    link: {
      title: t("richTextInput.link.title"),
      placeholder: t("richTextInput.link.placeholder"),
    },
    image: {
      title: t("richTextInput.image.title"),
      placeholder: t("richTextInput.image.placeholder"),
    },
    youtube: {
      title: t("richTextInput.youtube.title"),
      placeholder: t("richTextInput.youtube.placeholder"),
    },
  };

  const activeColor = editor?.getAttributes("textStyle").color ?? "#000000";
  const activeHighlight = editor?.getAttributes("highlight").color ?? "#dc0a23";

  return (
    <div
      className={classNames(styles.richTextInput, className, {
        [styles.disabled]: isDisabled,
        [styles.resizable]: isResizable,
      })}
    >
      {!isDisabled && (!autoToolbar || isFocused) && (
        <div className={styles.toolbar} ref={popoverRef}>
          <button
            type="button"
            title={t("richTextInput.toolbar.bold")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("bold"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleBold().run();
            }}
          >
            <BoldIcon className={styles.toolbarIcon} />
          </button>

          <button
            type="button"
            title={t("richTextInput.toolbar.italic")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("italic"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleItalic().run();
            }}
          >
            <ItalicIcon className={styles.toolbarIcon} />
          </button>

          <button
            type="button"
            title={t("richTextInput.toolbar.underline")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("underline"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleUnderline().run();
            }}
          >
            <UnderlineIcon className={styles.toolbarIcon} />
          </button>

          <button
            type="button"
            title={t("richTextInput.toolbar.strikethrough")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("strike"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleStrike().run();
            }}
          >
            <StrikethroughIcon className={styles.toolbarIcon} />
          </button>

          {mode === "full" && (
            <>
              <span className={styles.separator} />

              <button
                type="button"
                title={t("richTextInput.toolbar.alignLeft")}
                className={classNames(styles.toolbarBtn, {
                  [styles.active]: editor?.isActive({ textAlign: "left" }),
                })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().setTextAlign("left").run();
                }}
              >
                <AlignLeftIcon className={styles.toolbarIcon} />
              </button>

              <button
                type="button"
                title={t("richTextInput.toolbar.alignCenter")}
                className={classNames(styles.toolbarBtn, {
                  [styles.active]: editor?.isActive({ textAlign: "center" }),
                })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().setTextAlign("center").run();
                }}
              >
                <AlignCenterIcon className={styles.toolbarIcon} />
              </button>

              <button
                type="button"
                title={t("richTextInput.toolbar.alignRight")}
                className={classNames(styles.toolbarBtn, {
                  [styles.active]: editor?.isActive({ textAlign: "right" }),
                })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().setTextAlign("right").run();
                }}
              >
                <AlignRightIcon className={styles.toolbarIcon} />
              </button>
            </>
          )}

          <span className={styles.separator} />

          <button
            type="button"
            title={t("richTextInput.toolbar.bulletList")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("bulletList"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleBulletList().run();
            }}
          >
            <BulletListIcon className={styles.toolbarIcon} />
          </button>

          <button
            type="button"
            title={t("richTextInput.toolbar.orderedList")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("orderedList"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleOrderedList().run();
            }}
          >
            <OrderedListIcon className={styles.toolbarIcon} />
          </button>

          {mode === "full" && (
            <button
              type="button"
              title={t("richTextInput.toolbar.taskList")}
              className={classNames(styles.toolbarBtn, {
                [styles.active]: editor?.isActive("taskList"),
              })}
              onMouseDown={(e) => {
                e.preventDefault();
                editor?.chain().focus().toggleTaskList().run();
              }}
            >
              <TaskListIcon className={styles.toolbarIcon} />
            </button>
          )}

          <button
            type="button"
            title={t("richTextInput.toolbar.blockquote")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("blockquote"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleBlockquote().run();
            }}
          >
            <BlockquoteIcon className={styles.toolbarIcon} />
          </button>

          <span className={styles.separator} />

          <label
            className={styles.toolbarBtn}
            title={t("richTextInput.toolbar.textColor")}
          >
            <ColorTextIcon className={styles.toolbarIcon} color={activeColor} />
            <input
              type="color"
              className={styles.colorInput}
              value={activeColor}
              onChange={(e) =>
                editor?.chain().focus().setColor(e.target.value).run()
              }
            />
          </label>

          <label
            className={styles.toolbarBtn}
            title={t("richTextInput.toolbar.highlight")}
          >
            <HighlightIcon
              className={styles.toolbarIcon}
              color={activeHighlight}
            />
            <input
              type="color"
              className={styles.colorInput}
              value={activeHighlight}
              onChange={(e) =>
                editor
                  ?.chain()
                  .focus()
                  .toggleHighlight({ color: e.target.value })
                  .run()
              }
            />
          </label>

          <span className={styles.separator} />

          <button
            type="button"
            title={t("richTextInput.toolbar.codeBlock")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("codeBlock"),
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleCodeBlock().run();
            }}
          >
            <CodeBlockIcon className={styles.toolbarIcon} />
          </button>

          {mode === "full" && (
            <>
              <button
                type="button"
                title={t("richTextInput.toolbar.table")}
                className={styles.toolbarBtn}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor
                    ?.chain()
                    .focus()
                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                    .run();
                }}
              >
                <TableIcon className={styles.toolbarIcon} />
              </button>

              <span className={styles.separator} />

              <button
                type="button"
                title={t("richTextInput.toolbar.image")}
                className={classNames(styles.toolbarBtn, {
                  [styles.active]: popover === "image",
                })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  openPopover(popover === "image" ? null : "image");
                }}
              >
                <ImageIcon className={styles.toolbarIcon} />
              </button>
            </>
          )}

          <button
            type="button"
            title={t("richTextInput.toolbar.link")}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: editor?.isActive("link") || popover === "link",
            })}
            onMouseDown={(e) => {
              e.preventDefault();
              openPopover(popover === "link" ? null : "link");
            }}
          >
            <LinkIcon className={styles.toolbarIcon} />
          </button>

          {mode === "full" && (
            <button
              type="button"
              title={t("richTextInput.toolbar.youtube")}
              className={classNames(styles.toolbarBtn, {
                [styles.active]: popover === "youtube",
              })}
              onMouseDown={(e) => {
                e.preventDefault();
                openPopover(popover === "youtube" ? null : "youtube");
              }}
            >
              <YoutubeIcon className={styles.toolbarIcon} />
            </button>
          )}

          {popover && (
            <ToolbarPopover
              title={popoverLabels[popover].title}
              placeholder={popoverLabels[popover].placeholder}
              value={popoverValue}
              showRemove={popover === "link" && editor?.isActive("link")}
              onChange={setPopoverValue}
              onConfirm={confirmPopover}
              onClose={closePopover}
              onRemove={removeLink}
            />
          )}
        </div>
      )}

      <div ref={editorWrapperRef} className={styles.editorWrapper}>
        {mode === "full" && tableToolbarPos && editor && (
          <div
            className={styles.tableToolbar}
            style={{ top: tableToolbarPos.top, left: tableToolbarPos.left }}
          >
            <button
              type="button"
              title={t("richTextInput.toolbar.addColumnBefore")}
              className={styles.tableToolbarBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().addColumnBefore().run();
              }}
            >
              <AddColumnBeforeIcon className={styles.toolbarIcon} />
            </button>
            <button
              type="button"
              title={t("richTextInput.toolbar.addColumnAfter")}
              className={styles.tableToolbarBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().addColumnAfter().run();
              }}
            >
              <AddColumnAfterIcon className={styles.toolbarIcon} />
            </button>
            <button
              type="button"
              title={t("richTextInput.toolbar.deleteColumn")}
              className={styles.tableToolbarBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().deleteColumn().run();
              }}
            >
              <DeleteColumnIcon className={styles.toolbarIcon} />
            </button>
            <span className={styles.tableToolbarSeparator} />
            <button
              type="button"
              title={t("richTextInput.toolbar.addRowBefore")}
              className={styles.tableToolbarBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().addRowBefore().run();
              }}
            >
              <AddRowBeforeIcon className={styles.toolbarIcon} />
            </button>
            <button
              type="button"
              title={t("richTextInput.toolbar.addRowAfter")}
              className={styles.tableToolbarBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().addRowAfter().run();
              }}
            >
              <AddRowAfterIcon className={styles.toolbarIcon} />
            </button>
            <button
              type="button"
              title={t("richTextInput.toolbar.deleteRow")}
              className={styles.tableToolbarBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().deleteRow().run();
              }}
            >
              <DeleteRowIcon className={styles.toolbarIcon} />
            </button>
            <span className={styles.tableToolbarSeparator} />
            <button
              type="button"
              title={t("richTextInput.toolbar.deleteTable")}
              className={styles.tableToolbarBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().deleteTable().run();
              }}
            >
              <DeleteTableIcon className={styles.toolbarIcon} />
            </button>
          </div>
        )}
        <EditorContent
          editor={editor}
          className={classNames(styles.editorContent, {
            [styles.editorContentDisabled]: isDisabled,
          })}
        />
        {mentionPopup && (
          <div
            className={styles.mentionPopup}
            style={{ top: mentionPopup.top, left: mentionPopup.left }}
          >
            <MentionList
              ref={mentionListRef}
              items={mentionPopup.items}
              command={mentionPopup.command}
            />
          </div>
        )}
      </div>

      {!isDisabled && (!autoToolbar || isFocused) && (
        <span className={styles.charCount}>
          {charCount} / {maxLength}
        </span>
      )}
    </div>
  );
}

export { RichTextInput };
